import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppContext } from '../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiTrash2, FiChevronDown, FiStar, FiGift } = FiIcons;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const milestoneVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  }
};

const progressVariants = {
  hidden: { width: 0 },
  visible: (progress) => ({
    width: `${Math.min(progress, 100)}%`,
    transition: {
      duration: 1.5,
      ease: "easeOut"
    }
  })
};

const RewardPicker = ({ onSelect, onCancel }) => {
  const { getAllRewards, standardRewards, rewards } = useAppContext();
  const [selectedReward, setSelectedReward] = useState(null);
  const [targetNights, setTargetNights] = useState('');

  const handleSubmit = () => {
    if (!selectedReward || !targetNights) return;
    
    const target = parseInt(targetNights);
    if (target < 1) return;

    onSelect({
      target,
      rewardId: selectedReward.id,
      reward: selectedReward.name,
      description: selectedReward.description || ''
    });
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      'Entertainment': '🎬',
      'Food': '🍽️',
      'Gaming': '🎮',
      'Outdoor': '🌳',
      'Games': '🎲',
      'Bedtime': '🌙'
    };
    return iconMap[category] || '🎁';
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-800">Add Milestone Reward</h3>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiTrash2} className="text-gray-500" />
          </button>
        </div>

        {/* Target Nights Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Target Number of Consecutive Nights *
          </label>
          <input
            type="number"
            min="1"
            max="365"
            value={targetNights}
            onChange={(e) => setTargetNights(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="e.g., 7"
            required
          />
        </div>

        {/* Reward Selection */}
        <div className="space-y-6">
          {/* Standard Rewards */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <SafeIcon icon={FiStar} className="text-xl text-amber-500" />
              <h4 className="text-lg font-semibold text-gray-800">Standard Rewards</h4>
              <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium">
                Built-in
              </span>
            </div>
            
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {standardRewards.map((reward) => (
                <motion.div
                  key={reward.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedReward(reward)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                    selectedReward?.id === reward.id
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="text-lg">{getCategoryIcon(reward.category)}</div>
                    <span className="bg-amber-500 text-white px-2 py-1 rounded-full text-xs">
                      Standard
                    </span>
                  </div>
                  <h5 className="font-semibold text-gray-800 mb-1">{reward.name}</h5>
                  {reward.description && (
                    <p className="text-sm text-gray-600 mb-2">{reward.description}</p>
                  )}
                  <div className="text-xs text-gray-500">
                    Suggested: {reward.streakLength} nights
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Custom Rewards */}
          {rewards.length > 0 && (
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <SafeIcon icon={FiGift} className="text-xl text-purple-600" />
                <h4 className="text-lg font-semibold text-gray-800">My Custom Rewards</h4>
                <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs font-medium">
                  {rewards.length} created
                </span>
              </div>
              
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {rewards.map((reward) => (
                  <motion.div
                    key={reward.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedReward(reward)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                      selectedReward?.id === reward.id
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <SafeIcon icon={FiGift} className="text-lg text-purple-600" />
                      <span className="bg-purple-500 text-white px-2 py-1 rounded-full text-xs">
                        Custom
                      </span>
                    </div>
                    <h5 className="font-semibold text-gray-800 mb-1">{reward.name}</h5>
                    {reward.description && (
                      <p className="text-sm text-gray-600 mb-2">{reward.description}</p>
                    )}
                    <div className="text-xs text-gray-500">
                      Suggested: {reward.streakLength} nights
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={onCancel}
            className="px-6 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedReward || !targetNights}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            Add Milestone
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const StreakMilestones = ({ childId }) => {
  const { getChildById, addMilestone, deleteMilestone, getRewardById } = useAppContext();
  const [showRewardPicker, setShowRewardPicker] = useState(false);
  const child = getChildById(childId);

  if (!child) return null;

  const handleAddMilestone = (milestoneData) => {
    addMilestone(childId, milestoneData);
    setShowRewardPicker(false);
  };

  const getRewardDisplay = (milestone) => {
    if (milestone.rewardId) {
      const reward = getRewardById(milestone.rewardId);
      return reward ? reward.name : milestone.reward;
    }
    return milestone.reward;
  };

  return (
    <>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">Streak Milestones & Rewards</h3>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowRewardPicker(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
          >
            <SafeIcon icon={FiPlus} />
            <span>Add Milestone</span>
          </motion.button>
        </div>

        <div className="space-y-6">
          {child.milestones.map((milestone, index) => {
            const progress = (child.currentStreak / milestone.target) * 100;
            
            return (
              <motion.div
                key={milestone.id}
                variants={milestoneVariants}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {milestone.target} consecutive nights
                    </h4>
                    <div className="flex items-center space-x-2 text-orange-600 mt-1">
                      <span>🎁</span>
                      <span className="font-medium">{getRewardDisplay(milestone)}</span>
                    </div>
                    {milestone.description && (
                      <p className="text-sm text-gray-500 mt-1">{milestone.description}</p>
                    )}
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => deleteMilestone(childId, milestone.id)}
                    className="text-red-400 hover:text-red-600 transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </motion.button>
                </div>

                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Progress</span>
                  <span>{child.currentStreak}/{milestone.target}</span>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <motion.div
                    variants={progressVariants}
                    custom={progress}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full relative"
                  >
                    {progress >= 100 && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 2 }}
                        className="absolute right-1 top-0 h-full flex items-center"
                      >
                        <span className="text-white text-xs">🎉</span>
                      </motion.div>
                    )}
                  </motion.div>
                </div>
              </motion.div>
            );
          })}

          {child.milestones.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-8 text-gray-500"
            >
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-4xl mb-2"
              >
                🎯
              </motion.div>
              <p>No milestones set yet.</p>
              <p className="text-sm">Add your first milestone to start tracking progress!</p>
            </motion.div>
          )}
        </div>
      </motion.div>

      <AnimatePresence>
        {showRewardPicker && (
          <RewardPicker
            onSelect={handleAddMilestone}
            onCancel={() => setShowRewardPicker(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default StreakMilestones;